import { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';

export default function NotificationSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    email: true,
    reminders: {
      enabled: true,
      days: [7, 3, 1],
    },
    communityTips: true,
    partnerActivity: true,
  });

  useEffect(() => {
    if (user) {
      const loadPreferences = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (data.preferences?.notifications) {
              setPreferences({
                email: data.preferences.notifications.email ?? true,
                reminders: data.preferences.notifications.reminders ?? {
                  enabled: true,
                  days: [7, 3, 1],
                },
                communityTips: data.preferences.notifications.communityTips ?? true,
                partnerActivity: data.preferences.notifications.partnerActivity ?? true,
              });
            }
          }
        } catch (error) {
          console.error('Error loading preferences:', error);
        }
      };
      loadPreferences();
    }
  }, [user]);

  const handleToggle = (key) => {
    setPreferences({
      ...preferences,
      [key]: !preferences[key],
    });
  };

  const handleReminderDays = (day) => {
    const days = preferences.reminders.days;
    if (days.includes(day)) {
      setPreferences({
        ...preferences,
        reminders: {
          ...preferences.reminders,
          days: days.filter(d => d !== day),
        },
      });
    } else {
      setPreferences({
        ...preferences,
        reminders: {
          ...preferences.reminders,
          days: [...days, day].sort((a, b) => b - a),
        },
      });
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      const currentData = userDoc.exists() ? userDoc.data() : {};

      await updateDoc(userDocRef, {
        preferences: {
          ...currentData.preferences,
          notifications: preferences,
        },
        updatedAt: new Date(),
      });

      toast.success('Notification settings updated!');
    } catch (error) {
      console.error('Error updating notifications:', error);
      toast.error('Failed to update notification settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-card shadow-card border border-border p-6 sm:p-8">
      <h2 className="text-xl sm:text-2xl font-semibold text-text-primary mb-6">Notification Preferences</h2>

      {/* Email Notifications */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <label className="block text-sm font-medium text-text-primary">Email Notifications</label>
            <p className="text-xs text-text-secondary">Receive email updates about your account</p>
          </div>
          <button
            onClick={() => handleToggle('email')}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              preferences.email ? 'bg-primary' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                preferences.email ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Event Reminders */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <label className="block text-sm font-medium text-text-primary">Upcoming Event Reminders</label>
            <p className="text-xs text-text-secondary">Get notified before your events</p>
          </div>
          <button
            onClick={() => setPreferences({
              ...preferences,
              reminders: { ...preferences.reminders, enabled: !preferences.reminders.enabled }
            })}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              preferences.reminders.enabled ? 'bg-primary' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                preferences.reminders.enabled ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
        {preferences.reminders.enabled && (
          <div className="ml-0 pl-0 space-y-2">
            <p className="text-xs text-text-secondary mb-2">Remind me:</p>
            {[7, 3, 1].map((day) => (
              <label key={day} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={preferences.reminders.days.includes(day)}
                  onChange={() => handleReminderDays(day)}
                  className="w-4 h-4 text-primary rounded"
                />
                <span className="text-sm text-text-primary">{day} day{day !== 1 ? 's' : ''} before</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Community Tips */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <label className="block text-sm font-medium text-text-primary">New Community Tips</label>
            <p className="text-xs text-text-secondary">Get notified when new tips are shared</p>
          </div>
          <button
            onClick={() => handleToggle('communityTips')}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              preferences.communityTips ? 'bg-primary' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                preferences.communityTips ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Partner Activity */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <label className="block text-sm font-medium text-text-primary">Partner Activity</label>
            <p className="text-xs text-text-secondary">Get notified when your partner updates shared experiences</p>
          </div>
          <button
            onClick={() => handleToggle('partnerActivity')}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              preferences.partnerActivity ? 'bg-primary' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                preferences.partnerActivity ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={loading}
        className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon icon={faSave} />
        <span>{loading ? 'Saving...' : 'Save Changes'}</span>
      </button>
    </div>
  );
}

